/**
 * BattleCode V2 — 地图工具
 *
 * FR-018 ADR-018.8:cover positions 从 map JSON 自动提取
 *   之前 pre-flight-scenarios.ts 手抄 24 个 [x, y]，与 battle_easy.json 双重真相
 *   改地图忘改 pre-flight → bot 看到的 cover ≠ 屏 4 渲染的 cover
 *
 *   现在：单一来源 = map JSON 文件(屏 2/屏 4/引擎/bot 全部从这里取)
 *
 * V2 多地图注册中心（2026-07-10 重构）:
 *   - 地图源从 data/maps/*.json → data/levels/_builtin/[level_id]/map.json
 *   - 5 个内置 Level 的地图(battle_easy 独立 + L0-L4)
 *   - 所有 extractor 函数 mapId 可选,不传 = battle_easy(向后兼容)
 *   - 屏 2 下拉 / 屏 4 加载 / 引擎 preflight 都用 `getMapById(id)`
 *
 * Runtime Registry（2026-07-08 重构）:
 *   - _user 自建关卡不在 MAP_REGISTRY 中，通过 registerRuntimeMap 注入
 *   - screen-4 从 LevelStore 加载 LevelData 后调 registerRuntimeMap
 *   - extractors 先查 RUNTIME_MAPS，再查 MAP_REGISTRY，最后 fallback battle_easy
 *   - 引擎 30Hz tick 同步调用 extractors，无需异步化
 */

import battleEasyMap from "../../../data/levels/_builtin/battle_easy/map.json";
// V2 Level 地图: data/levels/_builtin/[level_id]/map.json
import mapL0 from "../../../data/levels/_builtin/level_0_first_contact/map.json";
import mapL1 from "../../../data/levels/_builtin/level_1_open_duel/map.json";
import mapL2 from "../../../data/levels/_builtin/level_2_cover_fire/map.json";
import mapL3 from "../../../data/levels/_builtin/level_3_tactical_hold/map.json";
import mapL4 from "../../../data/levels/_builtin/level_4_lateral_strike/map.json";
// V3: 3v3 小队竞技场
import map3v3 from "../../../data/levels/_builtin/battle_3v3/map.json";
// V3 Showcase: 2v2 双人协同
import mapShowcase2v2 from "../../../data/levels/_builtin/showcase_2v2/map.json";

import { CHAR_TO_TILE, NUM_TO_CHAR } from "./tile-codec";

export type MapTileChar = "." | "#" | "C" | "S" | "X";
export type CoverPosition = [number, number];

// ============================================================
// Runtime Registry — _user 自建关卡注入
// ============================================================

/** registerRuntimeMap 的输入（与 LevelStore.MapData 结构兼容，鸭子类型） */
export interface RuntimeMapInput {
  tiles: number[][];          // LevelStore 客户端格式
  width: number;
  height: number;
  spawnA: [number, number];
  spawnB: [number, number];
}

/** Runtime map 条目（转换为磁盘 string[] 格式，与 MAP_REGISTRY 条目结构对齐） */
interface RuntimeMapEntry {
  tiles: string[];
  dimensions: { width: number; height: number };
  spawns: {
    team_a: Array<{ x: number; y: number }>;
    team_b: Array<{ x: number; y: number }>;
  };
}

const RUNTIME_MAPS = new Map<string, RuntimeMapEntry>();

/**
 * 注册运行时地图（_user 自建关卡）。
 * screen-4 从 LevelStore 加载 LevelData 后调用此函数，将 MapData(number[][]) 转换为
 * 磁盘格式(string[])并注入 RUNTIME_MAPS。之后 engine-scheduler 30Hz tick 中
 * extractCoverPositions(mapId) / isObstacleAt(x,y,mapId) 即可拿到正确数据。
 */
export function registerRuntimeMap(mapId: string, map: RuntimeMapInput): void {
  const tiles = map.tiles.map(row =>
    row.map(n => NUM_TO_CHAR[n] ?? '.').join('')
  );
  RUNTIME_MAPS.set(mapId, {
    tiles,
    dimensions: { width: map.width, height: map.height },
    spawns: {
      team_a: [{ x: map.spawnA[0], y: map.spawnA[1] }],
      team_b: [{ x: map.spawnB[0], y: map.spawnB[1] }],
    },
  });
  _cache.delete(mapId); // 清旧缓存，下次 getCached 重建
}

// ============================================================
// V2 多地图注册中心
// ============================================================
/** 地图注册表(Level ID → JSON data)。
 *  新增地图:在 data/levels/_builtin/[level_id]/ 放 map.json,这里 import + 注册即可。 */
const MAP_REGISTRY: Record<string, any> = {
  // demo 主图（已迁移到 _builtin）
  battle_easy: battleEasyMap,
  // V2 Level 地图
  level_0_first_contact: mapL0,
  level_1_open_duel: mapL1,
  level_2_cover_fire: mapL2,
  level_3_tactical_hold: mapL3,
  level_4_lateral_strike: mapL4,
  // V3: 3v3 小队竞技场
  battle_3v3: map3v3,
  // V3 Showcase: 2v2 双人协同
  showcase_2v2: mapShowcase2v2,
  // 旧 ID 兼容别名（屏 2 等仍可能用旧 map id 引用）
  battle_l1_open: mapL1,
  battle_l2_few_covers: mapL2,
  battle_l3_corridor: mapL3,
  battle_l4_islands: mapL4,
};

/** 屏 2 UI 下拉的地图列表(展示用 metadata,顺序就是下拉顺序) */
export const AVAILABLE_MAPS: Array<{
  id: string;
  name: string;
  coverCount: number;
  obstacleCount: number;
  difficulty: "demo" | "L0" | "L1" | "L2" | "L3" | "L4" | "2v2" | "3v3";
}> = [
  { id: "battle_easy", name: "平原对决 · 演示主图", coverCount: 24, obstacleCount: 12, difficulty: "demo" },
  { id: "battle_3v3", name: "三对三 · 小队竞技场 (3v3)", coverCount: 24, obstacleCount: 12, difficulty: "3v3" },
  { id: "showcase_2v2", name: "二对二 · 双人协同 (2v2 展示)", coverCount: 12, obstacleCount: 4, difficulty: "2v2" },
  { id: "level_0_first_contact", name: "初次接触 · 教学关 (15×15)", coverCount: 0, obstacleCount: 0, difficulty: "L0" },
  { id: "level_1_open_duel", name: "空旷对决 (0 掩体/0 障碍)", coverCount: 0, obstacleCount: 0, difficulty: "L1" },
  { id: "level_2_cover_fire", name: "掩体火力 (4 掩体/0 障碍)", coverCount: 4, obstacleCount: 0, difficulty: "L2" },
  { id: "level_3_tactical_hold", name: "战术坚守 (8 掩体/4 障碍)", coverCount: 8, obstacleCount: 4, difficulty: "L3" },
  { id: "level_4_lateral_strike", name: "侧翼突击 (12 掩体/4 障碍)", coverCount: 12, obstacleCount: 4, difficulty: "L4" },
];

/** 根据 id 拿地图 JSON,查找顺序: runtime → static → fallback battle_easy(永不返回 null) */
export function getMapById(id: string): any {
  return RUNTIME_MAPS.get(id) ?? MAP_REGISTRY[id] ?? MAP_REGISTRY.battle_easy;
}

/** 默认地图 id(向后兼容所有未传 mapId 的旧调用) */
export const DEFAULT_MAP_ID = "battle_easy";

// ============================================================
// FR-018 兼容层:所有 extractor 函数接受可选 mapId
// ============================================================
function getTiles(mapId?: string): string[] {
  const map = getMapById(mapId ?? DEFAULT_MAP_ID);
  return (map as any).tiles as string[];
}

/** 缓存:避免每次都重新提取 */
const _cache = new Map<
  string,
  {
    cover: CoverPosition[];
    all: { cover: CoverPosition[]; obstacle: CoverPosition[]; spawn: CoverPosition[]; unreachable: CoverPosition[] };
  }
>();

function getCached(mapId?: string) {
  const key = mapId ?? DEFAULT_MAP_ID;
  const hit = _cache.get(key);
  if (hit) return hit;
  const tiles = getTiles(key);
  const cover: CoverPosition[] = [];
  const all = { cover: [], obstacle: [], spawn: [], unreachable: [] } as {
    cover: CoverPosition[]; obstacle: CoverPosition[]; spawn: CoverPosition[]; unreachable: CoverPosition[];
  };
  for (let y = 0; y < tiles.length; y++) {
    const row = tiles[y];
    for (let x = 0; x < row.length; x++) {
      const type = CHAR_TO_TILE[row[x]] ?? "empty";
      if (type === "cover") {
        cover.push([x, y]);
        all.cover.push([x, y]);
      } else if (type !== "empty") {
        all[type].push([x, y]);
      }
    }
  }
  const result = { cover, all };
  _cache.set(key, result);
  return result;
}

/**
 * 从指定地图提取所有 cover 位置 [x, y]
 *   不传 mapId = battle_easy(向后兼容)
 */
export function extractCoverPositions(mapId?: string): CoverPosition[] {
  return getCached(mapId).cover;
}

/**
 * 提取指定地图的所有 4 类 tile 位置(用于碰撞检测扩展等)
 */
export function extractAllTilePositions(mapId?: string): {
  cover: CoverPosition[];
  obstacle: CoverPosition[];
  spawn: CoverPosition[];
  unreachable: CoverPosition[];
} {
  return getCached(mapId).all;
}

/**
 * 判断某格是否是障碍物/不可达(阻挡 move)
 *   包含 cover？默认不阻挡(battle_easy.json 中 cover 是掩体可走入)
 *   v1+ 改读 tile.blocks_movement 字段
 */
export function isObstacleAt(x: number, y: number, mapId?: string): boolean {
  const positions = extractAllTilePositions(mapId);
  return (
    positions.obstacle.some(([ox, oy]) => ox === x && oy === y) ||
    positions.unreachable.some(([ux, uy]) => ux === x && uy === y)
  );
}

/**
 * 清空缓存(测试用 + 关卡切换后防残留)
 */
export function clearMapCache(): void {
  _cache.clear();
  RUNTIME_MAPS.clear();
}
